import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import { GetOrdersApiParameter } from "@insite/client-framework/Services/OrderService";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import { getSession, getSettingsCollection } from "@insite/client-framework/Store/Context/ContextSelectors";
import loadOrders from "@insite/client-framework/Store/Data/Orders/Handlers/LoadOrders";
import { getOrdersDataView } from "@insite/client-framework/Store/Data/Orders/OrdersSelectors";
import { getPageLinkByPageType } from "@insite/client-framework/Store/Links/LinksSelectors";
import translate from "@insite/client-framework/Translate";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import CardContainer, { CardContainerStyles } from "@insite/content-library/Components/CardContainer";
import CardList, { CardListStyles } from "@insite/content-library/Components/CardList";
// import CardListHeading from "@insite/content-library/Components/CardListHeading";
import GridItem, { GridItemProps } from "@insite/mobius/GridItem";
import Link from "@insite/mobius/Link";
import LoadingSpinner, { LoadingSpinnerProps } from "@insite/mobius/LoadingSpinner";
import Typography, { TypographyPresentationProps } from "@insite/mobius/Typography";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import CardListHeading from "@MasterBluePrint/Overrides/Components/CardListHeading";
import OrderSummaryCard, { OrderSummaryCardStyles } from "@MasterBluePrint/Overrides/Components/OrderSummaryCard";
import * as React from "react";
import { connect, ResolveThunks } from "react-redux";
import { css } from "styled-components";

const enum fields {
    numberOfRecords = "numberOfRecords",
}


interface OwnProps extends WidgetProps {
    fields: {
        [fields.numberOfRecords]: number;
    };
}

const mapStateToProps = (state: ApplicationState) => {
    const session = getSession(state);
    const isAuthenticated = session.isAuthenticated && !session.isGuest;
    return {
        ordersDataView: getOrdersDataView(state, recentOrdersParameter),
        orderHistoryPageLink: getPageLinkByPageType(state, "OrderHistoryPage"),
        settingsCollection: getSettingsCollection(state),
        canViewOrders: isAuthenticated && (session.userRoles || "").indexOf("Requisitioner") === -1,
        myAccountPageUrl: getPageLinkByPageType(state, "MyAccountPage")?.url,
    };
};

const mapDispatchToProps = {
    loadOrders,
};

type Props = OwnProps & ReturnType<typeof mapStateToProps> & ResolveThunks<typeof mapDispatchToProps>;

export interface RecentOrdersStyles {
    cardList?: CardListStyles;
    errorGridItem?: GridItemProps;
    errorText?: TypographyPresentationProps;
    noOrdersFoundGridItem?: GridItemProps;
    noOrdersFoundText?: TypographyPresentationProps;
    cardContainer?: CardContainerStyles;
    orderSummaryCard?: OrderSummaryCardStyles;
    RecentOrdersWrapper?: InjectableCss;
    noOrderDate?: InjectableCss;  
    noOrderText?: InjectableCss;  
    orderViewAllLink?: InjectableCss;
    spinner?:LoadingSpinnerProps;
    centeringWrapper?:InjectableCss;
    centeringLoaderWrapper?:InjectableCss;
}

export const recentOrdersStyles: RecentOrdersStyles = {
    errorGridItem: { width: 12 },
    noOrdersFoundGridItem: { 
        width: 12,
        css: css `
            flex-direction: column;

            @media screen and (min-width: 768px) {
                margin: 0 10px !important;
            }

            @media screen and (min-width: 767px) and (max-width: 990px) {
                padding-left: 10px;
                padding-right: 10px;
            }
        `,
    },
    RecentOrdersWrapper: {
        css: css`
            div.card_container__wrapper {
                background-color: #f7f9fa !important;
                box-shadow: none !important;
                border: none !important;
                padding-bottom: 0;

                > div {
                    padding-top: 0 !important;
                }

                @media screen and (min-width: 767px) and (max-width: 990px) {
                    > div {
                        padding-left: 10px;
                        padding-right: 10px;
                    }
                }

                @media screen and (max-width: 767px) {
                    > div {
                        padding-left: .625rem;
                        padding-right: .625rem;
                    }
                }
            }
        `
    },
    noOrderDate: {
        css: css`
            color: #002554;
            font-size: .875rem;
            letter-spacing: 0;
            line-height: 1rem;
            padding: 1.375rem .75rem .625rem .75rem;
            border-radius: 0 0 .125rem .125rem;
            background-color: #E5EEF7;
            width: 100%;
        `,
    },
    noOrderText: {
        css: css`
            color: #2E384D;
            font-family: 'Lato',sans-serif;
            font-size: .875rem;
            font-weight: bold;
            letter-spacing: 0;
            line-height: 1rem;
            padding: 1.25rem 1.563rem;
            background-color: #fff;
            width: 100%;
        `,
    },
    orderViewAllLink: {
        css: css`
            width: 70%;
            position: relative;
            text-align: center;
            margin: 1.175rem auto 2.5rem;
            text-transform: none;

            &:after {
                border-top: 1px solid #E4E8F0;
                content: '';
                display: block;
                position: absolute;
                top: 1.125rem;
                width: 100%;
            }
            
            span {
                color: #002554 !important;
                font-family: 'Roboto', sans-serif;
                text-transform: none;
                font-size: .875rem;
                font-weight: 500;
                letter-spacing: 1.31px;
                line-height: 1rem;
                text-align: center;
                padding: .625rem 2.5rem;
                background: #f7f9fa;
                position: relative;
                z-index: 1;
            }

            .disable-look {
                cursor: default;
                pointer-events: none;
                span {
                    color: #CBCBCB !important;
                }
            }

            @media screen and (max-width: 767px) {
                justify-content: flex-end;
                margin-left: auto;
                margin: .625rem auto 1.875rem;
            }
        `,
    },
    centeringWrapper: {
        css: css`
            height: 18.75rem;
            display: flex;
            align-items: center;
        `,
    },
    centeringLoaderWrapper:{
        css: css`
            position: fixed;
            background: rgba(0, 37, 84, 0.2);
            width: 100%;
            height: 100%;
            display: flex;
            align-items: center;
            z-index: 99;
            top: 0;
            left: 0;

            svg {
                position: relative;
                z-index: 99;
            }
        `,
    },
    spinner: {
        css: css`
            margin: auto;
            color: #002554;
        `,
    },
};

let recentOrdersParameter: GetOrdersApiParameter = {
    page: 1,
    sort: "OrderDate DESC",
};



const styles = recentOrdersStyles;

class RecentOrders extends React.Component<Props> {

    componentDidMount() {
        recentOrdersParameter.pageSize = this.props.fields.numberOfRecords;
        if (this.props.canViewOrders && !this.props.ordersDataView.value) {
            this.props.loadOrders(recentOrdersParameter);
        }  
    }
   
    
    render() {        
        const { ordersDataView, orderHistoryPageLink, settingsCollection, canViewOrders, myAccountPageUrl } = this.props;
        if (!canViewOrders || !ordersDataView.value) {
            return (
                <StyledWrapper {...styles.centeringWrapper}>
                    <LoadingSpinner {...styles.spinner} />
                </StyledWrapper>
            );
        }
        const orderHistoryUrl = orderHistoryPageLink ? orderHistoryPageLink.url : undefined;
        return (
            <CardList
                css={css`
                    padding-bottom: 3.125rem;
                `}
                extendedStyles={styles.cardList}
                data-test-selector="widget_RecentOrders"
                {...styles.RecentOrdersWrapper}
            >
                <CardListHeading heading={translate("Latest Orders")} viewAllUrl={orderHistoryUrl} length={ordersDataView.value.length}  />              
                {ordersDataView.value.length === 0 && ( 
                    <GridItem {...styles.noOrdersFoundGridItem}>
                        <Typography as="div" {...styles.noOrderDate}>{translate("Date Requested")}</Typography>
                        <Typography {...styles.noOrderText} data-test-selector="recentOrders_NoOrdersFound">
                            {translate("No recent orders.")}
                        </Typography>
                    </GridItem>
                )}
                {ordersDataView.value.map(
                    order =>
                        order !== null && (
                            <CardContainer key={order.id} extendedStyles={styles.cardContainer}>
                                <OrderSummaryCard
                                    order={order}
                                    orderSettings={settingsCollection.orderSettings}
                                    extendedStyles={styles.orderSummaryCard}
                                />
                            </CardContainer>
                        ),
                )}
                {myAccountPageUrl ? 
                    <Typography as="div" {...styles.orderViewAllLink}>
                        <Link
                            href={orderHistoryUrl}
                            typographyProps={{ size: 14 }}
                            data-test-selector="recentOrdersViewAllLink"
                            className={`${ordersDataView.value.length === 0 ? 'disable-look' : ''}`} 
                        >
                            {translate("View all ")}
                        </Link>
                    </Typography>
                : '' }
            </CardList>
        );
    }
}

const widgetModule: WidgetModule = {
    component: connect(mapStateToProps, mapDispatchToProps)(RecentOrders),
    definition: {
        group: "Common",
        icon: "list-ul",
        fieldDefinitions: [
            {
                name: fields.numberOfRecords,
                displayName: "Number of Recent Orders Displayed",
                editorTemplate: "IntegerField",
                min: 1,
                defaultValue: 5,
                fieldType: "General",
                sortOrder: 1,
            },
        ],
    },
};

export default widgetModule;
